import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';
import { CreateTaskTypeVariablesSchema } from './use-create-task-type';
import { TaskTypeSchema } from './use-load-task-types';

const UpdateTaskTypeVariablesSchema = z.object({
  id: z.union([z.string(), z.number()]),
  data: CreateTaskTypeVariablesSchema.partial(),
});

export type UpdateTaskTypeVariables = z.infer<typeof UpdateTaskTypeVariablesSchema>;

export async function updateTaskTypeFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateTaskTypeVariables,
) {
  const parsed = UpdateTaskTypeVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  const { data } = await instance.put(`/task_types/${parsed.id}`, parsed.data);
  return TaskTypeSchema.parse(data);
}

export function useUpdateTaskType() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<z.infer<typeof TaskTypeSchema>, ApiError, UpdateTaskTypeVariables>({
    mutationFn: variables => updateTaskTypeFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['taskTypes'] });
    },
  });

  return addActionNameToMutationResult<
    z.infer<typeof TaskTypeSchema>,
    ApiError,
    UpdateTaskTypeVariables,
    'updateTaskType'
  >('updateTaskType', mutation);
}
